// src/components/CustomFields/ConversationLayers.tsx
// Multi-select layers picker with click-to-edit and auto-save.
// Fetches available layers from the API on open; persists via PUT conversation/UpdateLayers.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '@services/api';
import { socketService } from '@/services/socketService';
import { conversationBus } from '@/lib/conversationBus';
import CorgeeStorageHelper from '@/components/Globals/CorgeeStorageHelper';
import { getVisibleLayers, getLayersName } from '@/lib/arrayUtils';
import type { ConversationLayer, ConversationItem } from './types';

interface ConversationLayersProps {
  conversation: ConversationItem;
  isNew: boolean;
  disableAutoSave: boolean;
  onChangedLayers?: (conv: ConversationItem, isFromInfoWindow?: boolean) => void;
  addLayerToCard?: (e: React.MouseEvent, layer: ConversationLayer) => void;
  configData?: any;
  section?: string;
}

export default function ConversationLayers({
  conversation,
  isNew,
  disableAutoSave,
  onChangedLayers,
  addLayerToCard,
  configData,
}: ConversationLayersProps)
{
  const [editorEnabled, setEditorEnabled] = useState(false);
  const [layersSelected, setLayersSelected] = useState<ConversationLayer[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [originalSelectedIds, setOriginalSelectedIds] = useState<number[]>([]);
  const [originalSelectedLayers, setOriginalSelectedLayers] = useState<ConversationLayer[]>([]);
  const [dataLayers, setDataLayers] = useState<ConversationLayer[]>([]);
  const [canEdit, setCanEdit] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  // Refs so the click-outside handler always has current state
  const stateRef = useRef({
    selectedIds,
    originalSelectedIds,
    layersSelected,
    originalSelectedLayers,
    dataLayers,
  });
  stateRef.current = {
    selectedIds,
    originalSelectedIds,
    layersSelected,
    originalSelectedLayers,
    dataLayers,
  };

  // --- Initialise selected layers from conversation prop ---
  useEffect(() =>
  {
    if (conversation?.ConversationLayers)
    {
      const ids = conversation.ConversationLayers.map(l => l.LayerId);
      setLayersSelected(conversation.ConversationLayers);
      setSelectedIds(ids);
      setOriginalSelectedIds(ids);
      setOriginalSelectedLayers(conversation.ConversationLayers);
    }
  }, [conversation?.ConversationLayers]);

  // --- Compute canEdit ---
  useEffect(() =>
  {
    let canEditFixed = CorgeeStorageHelper.canEdit(configData);
    const personLayers = CorgeeStorageHelper.getAllLayers();
    if (personLayers.length === 1 && !isNew) canEditFixed = false;
    setCanEdit(canEditFixed);
  }, [configData, isNew]);

  // --- Close dropdown on outside click when open ---
  useEffect(() =>
  {
    if (!editorEnabled) return;

    const timeoutId = setTimeout(() =>
    {
      const handler = (e: MouseEvent) =>
      {
        if (containerRef.current && !containerRef.current.contains(e.target as Node))
        {
          performSave();
          setEditorEnabled(false);
        }
      };
      document.addEventListener('mousedown', handler);
      // Store ref for cleanup
      (containerRef as any).__handler = handler;
    }, 100);

    return () =>
    {
      clearTimeout(timeoutId);
      const h = (containerRef as any).__handler;
      if (h)
      {
        document.removeEventListener('mousedown', h);
        delete (containerRef as any).__handler;
      }
    };
  }, [editorEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Open editor: fetch layers from API ---
  const handleEnableEditor = async () =>
  {
    const configValueId = conversation.selectedConfigValueId || conversation.ConfigValueId;
    const projectId = conversation.ProjectId || conversation.ProjectID;

    let params = '?';
    if (configValueId) params += 'configValueId=' + configValueId;
    if (projectId) params += '&projectId=' + projectId;
    if (conversation.ConversationId) params += '&conversationId=' + conversation.ConversationId;

    try
    {
      let layers = await apiService.get<ConversationLayer[]>(
        'layer/GetLayersByConfigValueId' + params,
      );

      // For non-project conversations with a ProjectConversationId, merge project layers
      if (
        conversation.selectedConfigValueId !== 9 &&
        conversation.ConfigValueId !== 9 &&
        conversation.ProjectConversationId
      )
      {
        try
        {
          const layersForCard = await apiService.get<ConversationLayer[]>(
            `layer/GetLayersForCardBy?configValueId=${configValueId}&projectId=${projectId}`,
          );
          layers = mergeLayers(layers, layersForCard, conversation.ProjectConversationId);
        }
        catch
        {
          /* non-fatal */
        }
      }

      setDataLayers(getVisibleLayers(layers));
    }
    catch (err)
    {
      console.error('❌ Error loading layers:', err);
    }

    setEditorEnabled(true);
  };

  const mergeLayers = (
    current: ConversationLayer[],
    project: ConversationLayer[],
    projConvId: number,
  ): ConversationLayer[] =>
  {
    const map = new Map(current.map(l => [l.LayerId, l]));
    project.forEach(pl =>
    {
      if (!map.has(pl.LayerId)) map.set(pl.LayerId, pl);
    });
    return Array.from(map.values()).map(l => ({
      ...l,
      ProjectConversationId: projConvId,
      IsAvailable: current.some(cl => cl.LayerId === l.LayerId),
      disabled: !current.some(cl => cl.LayerId === l.LayerId),
    }));
  };

  // --- Toggle a layer in the selection ---
  const toggleLayer = (layer: ConversationLayer) =>
  {
    if (layer.disabled) return;
    setSelectedIds(prev =>
    {
      const exists = prev.includes(layer.LayerId);
      return exists ? prev.filter(id => id !== layer.LayerId) : [...prev, layer.LayerId];
    });
    setLayersSelected(prev =>
    {
      const exists = prev.some(l => l.LayerId === layer.LayerId);
      return exists ? prev.filter(l => l.LayerId !== layer.LayerId) : [...prev, layer];
    });
  };

  // --- Save selected layers ---
  const performSave = useCallback(() =>
  {
    const {
      selectedIds: sIds,
      originalSelectedIds: oIds,
      layersSelected: sLayers,
      originalSelectedLayers: oLayers,
      dataLayers: dLayers,
    } = stateRef.current;

    if (sIds.slice().sort().toString() === oIds.slice().sort().toString()) return;

    // isNew: propagate locally first
    if (isNew)
    {
      const conv = { ...conversation, ConversationLayers: sLayers };
      if (onChangedLayers) onChangedLayers(conv);
      setOriginalSelectedIds([...sIds]);
      setOriginalSelectedLayers([...sLayers]);
    }
    else if (disableAutoSave)
    {
      // multi-edition mode: notify parent without making an API call
      const enriched = sLayers
        .filter(l => l.LayerId !== 0)
        .map(l => ({
          ...l,
          LayerName: l.LayerName || l.Name || dLayers.find(d => d.LayerId === l.LayerId)?.Name || '',
          LayerColor: l.LayerColor || l.Color,
        }));
      const conv = { ...conversation, ConversationLayers: enriched };
      if (onChangedLayers) onChangedLayers(conv);
      setOriginalSelectedIds([...sIds]);
      setOriginalSelectedLayers([...sLayers]);
      return;
    }

    if (!disableAutoSave)
    {
      if ((!isNew && oIds.length > 0 && sLayers.length > 0) || isNew)
      {
        const conv: any = { ...conversation };
        conv.PreviousLayers = [...oIds].sort((a, b) => a - b).join(', ');
        conv.ConversationLayers = sLayers
          .filter(l => l.LayerId !== 0)
          .map(l => ({
            ...l,
            LayerName:
              l.LayerName || l.Name || dLayers.find(d => d.LayerId === l.LayerId)?.Name || '',
            LayerColor: l.LayerColor || l.Color,
          }));
        conv.SocketConnectionId = socketService.connectionId || configData?.SocketConnectionId;

        apiService
          .put<ConversationLayer[]>('conversation/UpdateLayers', conv)
          .then(response =>
          {
            const dLayers = stateRef.current.dataLayers;
            const layerColorMap = new Map(dLayers.map((l: any) => [l.LayerId, l.Color || l.LayerColor || '']));
            const updated = response.map((r: any) =>
            {
              const color = r.LayerColor || r.Color || layerColorMap.get(r.LayerId) || '';
              return {
                ConversationId: conversation.ConversationId,
                IsDefault: false,
                LayerColor: color,
                LayerId: r.LayerId,
                LayerName: r.LayerName || r.Name || '',
                Color: color,
              };
            });
            const finalConv = {
              ...conversation,
              ConversationLayers: updated.filter((l: any) => l.LayerId !== 0),
            };
            if (onChangedLayers) onChangedLayers(finalConv);
            conversationBus.emit('layers-updated', {
              conversationId: conversation.ConversationId,
              layers: finalConv.ConversationLayers,
            });            setOriginalSelectedIds(updated.map((l: any) => l.LayerId));
            setOriginalSelectedLayers(updated);
          })
          .catch(err => console.error('❌ Error saving layers:', err));
      }
      else
      {
        // Revert to original
        setLayersSelected(oLayers);
        setSelectedIds(oIds);
      }
    }
  }, [conversation, isNew, disableAutoSave, onChangedLayers, configData]);

  // --- Render ---
  const selectedNames = getLayersName(layersSelected);

  if (!canEdit)
  {
    return <div className="text-sm text-gray-600">{selectedNames}</div>;
  }

  if (editorEnabled)
  {
    return (
      <div ref={containerRef} className="relative">
        <div className="text-sm text-corgee-blue font-medium mb-1">{selectedNames}</div>
        <div className="absolute z-50 mt-0.5 bg-white border border-gray-200 rounded-md shadow-lg min-w-[180px] max-h-48 overflow-y-auto">
          {dataLayers.map(layer => (
            <div
              key={layer.LayerId}
              className={[
                'px-3 py-1.5 flex items-center gap-2 cursor-pointer hover:bg-gray-50',
                layer.disabled ? 'opacity-50' : '',
              ].join(' ')}
              onClick={() => toggleLayer(layer)}
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(layer.LayerId)}
                disabled={layer.disabled}
                className="rounded accent-corgee-blue"
                readOnly
              />
              <span
                className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: layer.Color }}
              />
              <span className="text-sm">{layer.Name}</span>
              {layer.disabled && addLayerToCard && (
                <button
                  className="ml-auto text-xs text-corgee-blue hover:underline"
                  onClick={e =>
                  {
                    e.stopPropagation();
                    addLayerToCard(e, layer);
                  }}
                >
                  Add?
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="lbl-editable-input" onClick={handleEnableEditor}>
      {selectedNames}
    </div>
  );
}
